import { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { toast } from 'react-toastify';
import { systemNotificationSseClient } from '../services/systemNotificationSse.js';
import { useAuthContext } from './AuthContext.jsx';
import { useMessageUnreadCount } from '../hooks/queries/messageQueries';
const SystemNotificationContext = createContext(null);

const initialState = {
  notifications: [], // 수신한 알림 목록
  status: 'idle', // SSE 연결 상태 (idle, connecting, open, error, closed 등)
  error: null,
};

const MAX_NOTIFICATIONS = 50; // 최대 50개까지만 메모리 유지

// 토스트에 출력할 메시지 문자열 생성
const formatToastMessage = (data) => {
  if (data == null) return '새 시스템 알림이 도착했습니다.';
  if (typeof data === 'string') return data;
  if (typeof data === 'object') {
    if (data.message) return String(data.message);
    if (data.title && data.body) return `${data.title} - ${data.body}`;
    return JSON.stringify(data);
  }
  return String(data);
};

// 컨텍스트 상태를 갱신하는 리듀서
function reducer(state, action) {
  switch (action.type) {
    case 'RESET':
      return { ...initialState };
    case 'STATUS':
      return {
        ...state,
        status: action.status,
        error: action.error ?? null,
      };
    case 'RECEIVE': {
      // 새 알림을 가장 앞에 추가하고 최대 개수 제한
      const nextNotifications = [action.payload, ...state.notifications].slice(
        0,
        MAX_NOTIFICATIONS
      );
      return {
        ...state,
        notifications: nextNotifications,
      };
    }
    case 'MARK_AS_READ': {
      const notifications = state.notifications.map((item) =>
        item.id === action.id ? { ...item, read: true } : item
      );
      const unreadCount = Math.max(
        0,
        notifications.filter((item) => !item.read).length
      );
      return {
        ...state,
        notifications,
        unreadCount,
      };
    }
    case 'MARK_ALL_AS_READ': {
      return {
        ...state,
        notifications: state.notifications.map((item) => ({
          ...item,
          read: true,
        })),
        unreadCount: 0,
      };
    }
    default:
      return state;
  }
}

export const SystemNotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { user, token, isAuthenticated } = useAuthContext();
  const { data: unreadCount, refetch: refetchUnreadCount } = useMessageUnreadCount(user?.UserSysId);
  // 인증 정보가 준비되면 SSE 연결을 시작한다.
  useEffect(() => {
    if (!isAuthenticated || !user?.UserSysId) {
      dispatch({ type: 'RESET' });
      systemNotificationSseClient.disconnect();
      return;
    }

    systemNotificationSseClient.connect(user.UserSysId, token);

    // 연결 상태 변경을 감지해서 상태값 갱신
    const unsubscribeStatus = systemNotificationSseClient.onStatusChange(
      (status, error) => {
        dispatch({ type: 'STATUS', status, error: error ?? null });
      }
    );

    // 메시지 수신 시 상태 업데이트 + 토스트 출력
    const unsubscribeMessages = systemNotificationSseClient.subscribe((event) => {
      const normalized = {
        id: event.id,
        type: event.type,
        data: event.payload,
        raw: event.raw,
        receivedAt: event.receivedAt,
        read: false,
      };
      dispatch({ type: 'RECEIVE', payload: normalized });
      if (user?.UserSysId) {
        refetchUnreadCount();
      }
      const message = formatToastMessage(normalized.data);
      toast.info(message, {
        toastId: `system-notification-${normalized.id}`,
        autoClose: false,
      });
    });

    return () => {
      // 컴포넌트 unmount 또는 인증 해제 시 정리
      unsubscribeStatus();
      unsubscribeMessages();
      // 리스너만 해제하고 연결은 유지 (route 변경 시에도 연결 유지)
      // disconnect는 useEffect의 조건문에서만 호출됨
    };
  }, [isAuthenticated, user?.UserSysId, token]);

  const value = useMemo(
    () => ({
      notifications: state.notifications,
      unreadCount: state.unreadCount,
      status: state.status,
      error: state.error,
      markAsRead: (id) => dispatch({ type: 'MARK_AS_READ', id }),
      markAllAsRead: () => dispatch({ type: 'MARK_ALL_AS_READ' }),
    }),
    [state]
  );

  return (
    <SystemNotificationContext.Provider value={value}>
      {children}
    </SystemNotificationContext.Provider>
  );
};

export const useSystemNotificationContext = () => {
  const context = useContext(SystemNotificationContext);
  if (!context) {
    throw new Error(
      'useSystemNotificationContext는 SystemNotificationProvider 내부에서만 사용할 수 있습니다.'
    );
  }
  return context;
};
